import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, Loader2, Pencil, Plus, Search } from "lucide-react";
import { AbigailBackdrop } from "@/components/abigail/AbigailBackdrop";
import { PlatformHeader } from "@/components/abigail/PlatformHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, ApiError } from "@/lib/api";
import { fetchMe, meQueryKey, type Me } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/admin/usuarios")({
  beforeLoad: async ({ context }) => {
    const cached = context.queryClient.getQueryData<Me | null>(meQueryKey);
    const me =
      cached ??
      (await context.queryClient.fetchQuery({ queryKey: meQueryKey, queryFn: fetchMe }));
    if (!me?.admin) throw redirect({ to: "/chat" });
  },
  component: AdminUsuariosPage,
});

type Usuario = {
  id: string;
  email: string;
  fullName: string | null;
  telefone: string | null;
  dataCompra: string | null;
  status: string | null;
  diagnostico: string | null;
  admin: boolean | null;
  source: string | null;
  createdAt: string;
};

const USUARIOS_KEY = ["admin", "usuarios"] as const;

function AdminUsuariosPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ email: string; senhaInicial: string } | null>(null);

  const { data: usuarios = [], isLoading } = useQuery({
    queryKey: USUARIOS_KEY,
    queryFn: () => api.get<Usuario[]>("/api/admin/usuarios"),
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/api/admin/usuarios/${vars.id}`, vars.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USUARIOS_KEY });
      setEditing(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { email: string; fullName: string; admin: boolean }) =>
      api.post<{ email: string; senhaInicial: string }>("/api/admin/usuarios", data),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: USUARIOS_KEY });
      setCreating(false);
      setCreated(data);
    },
  });

  const filtered = usuarios.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      (u.fullName ?? "").toLowerCase().includes(q) ||
      (u.telefone ?? "").toLowerCase().includes(q)
    );
  });

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("pt-BR");
  };

  return (
    <div className="relative flex min-h-screen flex-col">
      <AbigailBackdrop dim />
      <PlatformHeader />

      <main className="flex flex-1 flex-col px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-serif text-ember text-3xl">Usuários</h1>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou e-mail..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-64 border-border bg-background/60 pl-9 text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => window.open("/api/admin/usuarios/exportar", "_blank")}
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </Button>
              <Button
                size="sm"
                className="bg-ember shadow-ember gap-2 text-primary-foreground hover:opacity-90"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-4 w-4" />
                Criar usuário
              </Button>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card/80 shadow-card-deep backdrop-blur-md">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>Data Compra</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Admin</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                          Nenhum usuário encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.fullName ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground">{u.email}</TableCell>
                          <TableCell>{u.telefone ?? "—"}</TableCell>
                          <TableCell>{formatDate(u.dataCompra)}</TableCell>
                          <TableCell>
                            {u.status ? (
                              <Badge variant="outline" className="text-xs">
                                {u.status}
                              </Badge>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            {u.admin ? (
                              <Badge className="bg-ember text-xs text-primary-foreground">
                                Admin
                              </Badge>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditing(u)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {filtered.length} de {usuarios.length} usuário{usuarios.length !== 1 ? "s" : ""}
          </p>
        </div>
      </main>

      {editing ? (
        <EditDialog
          usuario={editing}
          saving={mutation.isPending}
          error={mutation.error instanceof ApiError ? mutation.error.message : null}
          onClose={() => setEditing(null)}
          onSave={(data) => mutation.mutate({ id: editing.id, data })}
        />
      ) : null}

      {creating ? (
        <CreateDialog
          saving={createMutation.isPending}
          error={createMutation.error instanceof ApiError ? createMutation.error.message : null}
          onClose={() => setCreating(false)}
          onCreate={(data) => createMutation.mutate(data)}
        />
      ) : null}

      {created ? (
        <CreatedDialog usuario={created} onClose={() => setCreated(null)} />
      ) : null}
    </div>
  );
}

function EditDialog({
  usuario,
  saving,
  error,
  onClose,
  onSave,
}: {
  usuario: Usuario;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
}) {
  const [fullName, setFullName] = useState(usuario.fullName ?? "");
  const [telefone, setTelefone] = useState(usuario.telefone ?? "");
  const [dataCompra, setDataCompra] = useState(
    usuario.dataCompra ? new Date(usuario.dataCompra).toISOString().slice(0, 10) : "",
  );
  const [status, setStatus] = useState(usuario.status ?? "");
  const [diagnostico, setDiagnostico] = useState(usuario.diagnostico ?? "");
  const [admin, setAdmin] = useState(usuario.admin ?? false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      fullName: fullName || null,
      telefone: telefone || null,
      dataCompra: dataCompra || null,
      status: status || null,
      diagnostico: diagnostico || null,
      admin,
    });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-border bg-card sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-ember text-xl">Editar Usuário</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <p className="text-sm text-foreground">{usuario.email}</p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-name" className="text-xs text-muted-foreground">
              Nome
            </Label>
            <Input
              id="edit-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-telefone" className="text-xs text-muted-foreground">
              Telefone
            </Label>
            <Input
              id="edit-telefone"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-data-compra" className="text-xs text-muted-foreground">
              Data da Compra
            </Label>
            <Input
              id="edit-data-compra"
              type="date"
              value={dataCompra}
              onChange={(e) => setDataCompra(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-status" className="text-xs text-muted-foreground">
              Status
            </Label>
            <Input
              id="edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-diagnostico" className="text-xs text-muted-foreground">
              Diagnóstico
            </Label>
            <Textarea
              id="edit-diagnostico"
              value={diagnostico}
              onChange={(e) => setDiagnostico(e.target.value)}
              className="min-h-[80px] border-border bg-background/60 text-sm"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch id="edit-admin" checked={admin} onCheckedChange={setAdmin} />
            <Label htmlFor="edit-admin" className="text-sm text-foreground">
              Administrador
            </Label>
          </div>

          {error ? (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-ember shadow-ember text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </form>

        <LiberarAvaliacao usuarioId={usuario.id} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Creates an account directly, no Hotmart purchase or pending_clients row
 * needed — this is how you invite someone (e.g. another admin) who never
 * bought through Hotmart. The account exists immediately, so no login flow
 * (senha, magic link, SSO) ever confronts it against a purchase again.
 */
function CreateDialog({
  saving,
  error,
  onClose,
  onCreate,
}: {
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (data: { email: string; fullName: string; admin: boolean }) => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [admin, setAdmin] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onCreate({ email: email.trim(), fullName: fullName.trim(), admin });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-border bg-card sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-ember text-xl">Criar Usuário</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground">
          Cria a conta direto, sem exigir uma compra no Hotmart. Uma senha inicial é gerada
          e mostrada uma única vez — repasse para a pessoa.
        </p>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="create-email" className="text-xs text-muted-foreground">
              Email
            </Label>
            <Input
              id="create-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="create-name" className="text-xs text-muted-foreground">
              Nome
            </Label>
            <Input
              id="create-name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-9 border-border bg-background/60 text-sm"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch id="create-admin" checked={admin} onCheckedChange={setAdmin} />
            <Label htmlFor="create-admin" className="text-sm text-foreground">
              Administrador
            </Label>
          </div>

          {error ? (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-ember shadow-ember text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Shows the generated initial password once — it's hashed server-side and can't be recovered later. */
function CreatedDialog({
  usuario,
  onClose,
}: {
  usuario: { email: string; senhaInicial: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(usuario.senhaInicial);
      setCopied(true);
    } catch {
      // clipboard access denied — the password stays visible on screen either way
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-ember text-xl">Usuário criado</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-foreground">
          Repasse esta senha inicial para <strong>{usuario.email}</strong>. Ela só aparece
          agora — depois de fechar, não é possível recuperá-la.
        </p>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/60 px-4 py-3">
          <code className="text-sm text-accent">{usuario.senhaInicial}</code>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => void handleCopy()}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        {copied ? <p className="text-xs text-accent">Copiado.</p> : null}

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            onClick={onClose}
            className="bg-ember shadow-ember text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Escape hatch for the journey limit. Completion is detected from Abigail's own
 * wording, so a reply phrased differently can leave someone unable to start over —
 * and, once the limit is reached, stuck in a read-only chat. This closes the open
 * journey and grants an extra evaluation when the limit is what's blocking.
 */
function LiberarAvaliacao({ usuarioId }: { usuarioId: string }) {
  const [resultado, setResultado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ mensagem: string }>(`/api/admin/usuarios/${usuarioId}/liberar-avaliacao`),
    onSuccess: (data) => {
      setErro(null);
      setResultado(data.mensagem);
    },
    onError: (err) => {
      setResultado(null);
      setErro(err instanceof Error ? err.message : "Não foi possível liberar.");
    },
  });

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-foreground">Liberar nova avaliação</p>
          <p className="text-xs text-muted-foreground">
            Use se a pessoa ficou sem conseguir recomeçar.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Liberar"}
        </Button>
      </div>
      {resultado ? <p className="mt-2 text-xs text-accent">{resultado}</p> : null}
      {erro ? <p className="mt-2 text-xs text-destructive">{erro}</p> : null}
    </div>
  );
}
